import { useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import type { Field } from "@project/admin/components/resource-table";
import { patientRecordService } from "@project/services/admin/patient-record.service";
import { treatmentSessionService } from "@project/services/admin/treatment-session.service";
import { referralTransferService } from "@project/services/admin/referral-transfer.service";
import type { PatientRecord, PatientRecordPayload } from "@project/types/patient-record";
import type { TreatmentSession, TreatmentSessionPayload } from "@project/types/treatment-session";
import type { ReferralTransfer, ReferralTransferPayload } from "@project/types/referral-transfer";
import { UserRound, Stethoscope, Ambulance } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@project/components/ui/tabs";


// ─── Patient Records ───────────────────────────────────────────────────────────

const patientFields: Field[] = [
  {
    name: "hospital",
    label: "Hospital",
    required: true,
    showInTable: true,
    placeholder: "e.g. ABD",
  },
  {
    name: "file_number",
    label: "File Number",
    required: true,
    showInTable: true,
    placeholder: "e.g. MED-2026-07",
  },
  {
    name: "full_name",
    label: "Full Name",
    required: true,
    showInTable: true,
    placeholder: "e.g. Rajesh Patel",
  },
  {
    name: "age",
    label: "Age",
    type: "number",
    required: true,
    showInTable: true,
    placeholder: "e.g. 72",
  },
  {
    name: "gender",
    label: "Gender",
    type: "select",
    required: true,
    showInTable: true,
    options: [
      { value: "Male", label: "Male" },
      { value: "Female", label: "Female" },
      { value: "Other", label: "Other" },
    ],
  },
  {
    name: "blood_group",
    label: "Blood Group",
    type: "select",
    showInTable: true,
    options: [
      { value: "A+", label: "A+" },
      { value: "A-", label: "A-" },
      { value: "B+", label: "B+" },
      { value: "B-", label: "B-" },
      { value: "O+", label: "O+" },
      { value: "O-", label: "O-" },
      { value: "AB+", label: "AB+" },
      { value: "AB-", label: "AB-" },
    ],
  },
  {
    name: "room_number",
    label: "Room Number",
    showInTable: true,
    placeholder: "e.g. Room 105",
  },
  {
    name: "patient_status",
    label: "Patient Status",
    type: "select",
    required: true,
    showInTable: true,
    options: [
      { value: "Admitted", label: "Admitted" },
      { value: "Discharged", label: "Discharged" },
      { value: "Under Observation", label: "Under Observation" },
    ],
  },
  {
    name: "diagnosis",
    label: "Diagnosis",
    showInTable: true,
    placeholder: "e.g. General Senior Care",
  },
  {
    name: "attending_doctor",
    label: "Attending Doctor",
    showInTable: true,
    placeholder: "e.g. Dr. Rajeshbhai Shah",
  },
  {
    name: "emergency_contact",
    label: "Emergency Contact",
    type: "tel",
    showInTable: false,
    placeholder: "e.g. +919825012345",
  },
  {
    name: "medical_history",
    label: "Medical History",
    type: "textarea",
    showInTable: false,
    placeholder: "e.g. Diabetes, BP",
  },
];

function toPatientPayload(payload: Record<string, unknown>): PatientRecordPayload {
  return {
    hospital: String(payload.hospital ?? "").trim(),
    file_number: String(payload.file_number ?? "").trim(),
    full_name: String(payload.full_name ?? "").trim(),
    age: parseInt(String(payload.age ?? "0"), 10) || 0,
    gender: (payload.gender as PatientRecord["gender"]) || "Male",
    blood_group: String(payload.blood_group ?? "").trim(),
    room_number: String(payload.room_number ?? "").trim(),
    patient_status:
      (payload.patient_status as PatientRecord["patient_status"]) || "Admitted",
    diagnosis: String(payload.diagnosis ?? "").trim(),
    attending_doctor: String(payload.attending_doctor ?? "").trim(),
    emergency_contact: String(payload.emergency_contact ?? "").trim(),
    medical_history: String(payload.medical_history ?? "").trim(),
  };
}

// ─── Treatment Sessions ────────────────────────────────────────────────────────
const treatmentFields: Field[] = [
  {
    name: "date",
    label: "Date",
    type: "date",
    required: true,
    showInTable: true,
  },
  {
    name: "patient_name",
    label: "Patient Name",
    required: true,
    showInTable: true,
    placeholder: "e.g. Popatbhai Shah",
  },
  {
    name: "care_section",
    label: "Care Section / Hospital",
    required: true,
    showInTable: true,
    placeholder: "e.g. Bhagwan Mahavir Medical Centre",
  },
  {
    name: "treatment_details",
    label: "Treatment Details",
    type: "textarea",
    required: true,
    showInTable: true,
    placeholder: "e.g. Right knee traction therapy session",
  },
  {
    name: "doctor_assigned",
    label: "Doctor Assigned",
    showInTable: true,
    placeholder: "e.g. Dr. Rajesh Shah",
  },
  {
    name: "notes",
    label: "Notes",
    type: "textarea",
    showInTable: false,
    placeholder: "e.g. Patient felt better after the session.",
  },
];

function toTreatmentPayload(payload: Record<string, unknown>): TreatmentSessionPayload {
  return {
    date: String(payload.date ?? "").trim(),
    patient_name: String(payload.patient_name ?? "").trim(),
    care_section: String(payload.care_section ?? "").trim(),
    treatment_details: String(payload.treatment_details ?? "").trim(),
    doctor_assigned: String(payload.doctor_assigned ?? "").trim(),
    notes: String(payload.notes ?? "").trim(),
  };
}

// ─── Referral Transfers ────────────────────────────────────────────────────────
const transferFields: Field[] = [
  {
    name: "date",
    label: "Date",
    type: "date",
    required: true,
    showInTable: true,
  },
  {
    name: "patient_name",
    label: "Patient Name",
    required: true,
    showInTable: true,
    placeholder: "e.g. Champaben Trivedi",
  },
  {
    name: "room_number",
    label: "Room Number",
    showInTable: true,
    placeholder: "e.g. Room 204",
  },
  {
    name: "referral_status",
    label: "Referral Status",
    type: "select",
    required: true,
    showInTable: true,
    options: [
      { value: "Hospitalized", label: "Hospitalized" },
      { value: "Discharged", label: "Discharged" },
      { value: "Under Observation", label: "Under Observation" },
    ],
  },
  {
    name: "partner_hospital",
    label: "Partner Hospital",
    required: true,
    showInTable: true,
    placeholder: "e.g. Sterling Multispeciality Hospital",
  },
  {
    name: "referral_reason",
    label: "Referral Reason",
    type: "textarea",
    showInTable: true,
    placeholder: "e.g. Acute Angina & Cardiac Angiography",
  },
  {
    name: "coordinator_name",
    label: "Coordinator Name",
    showInTable: true,
    placeholder: "e.g. Dr. Sejal Shah",
  },
];

function toTransferPayload(payload: Record<string, unknown>): ReferralTransferPayload {
  return {
    date: String(payload.date ?? "").trim(),
    patient_name: String(payload.patient_name ?? "").trim(),
    room_number: String(payload.room_number ?? "").trim(),
    referral_status:
      (payload.referral_status as ReferralTransfer["referral_status"]) ||
      "Hospitalized",
    partner_hospital: String(payload.partner_hospital ?? "").trim(),
    referral_reason: String(payload.referral_reason ?? "").trim(),
    coordinator_name: String(payload.coordinator_name ?? "").trim(),
  };
}

// ─── Tab Configuration ─────────────────────────────────────────────────────────
type TabId = "patients" | "treatments" | "transfers";

const tabs: { id: TabId; label: string; icon: typeof UserRound }[] = [
  { id: "patients", label: "Patient Records", icon: UserRound },
  { id: "treatments", label: "Treatment Register", icon: Stethoscope },
  { id: "transfers", label: "Hospital Transfers", icon: Ambulance },
];

// ─── Page ──────────────────────────────────────────────────────────────────────
export default function HealthcareManagementPage() {
  const [activeTab, setActiveTab] = useState<TabId>("patients");

  return (
    <AdminShell
      title="Healthcare Management"
      subtitle="Patient records, treatment sessions & hospital transfers"
    >
      <Tabs value={activeTab} onValueChange={(val) => setActiveTab(val as TabId)} variant="line" className="space-y-4">
        <div className="-mx-1 overflow-x-auto px-1 pb-1">
          <TabsList>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger key={tab.id} value={tab.id} className="shrink-0">
                  <Icon className="h-4 w-4 shrink-0" />
                  {tab.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {/* Tab Content */}
        {activeTab === "patients" && (
        <ApiResourceTable<PatientRecord>
          title="Patient Records"
          labelField="full_name"
          fields={patientFields}
          searchFields={["full_name", "file_number", "attending_doctor", "diagnosis"]}
          defaultValues={{
            gender: "Male",
            patient_status: "Admitted",
            blood_group: "O+",
            age: 60,
          }}
          statusFilterOptions={[
            { value: "Admitted", label: "Admitted" },
            { value: "Discharged", label: "Discharged" },
            { value: "Under Observation", label: "Under Observation" },
          ]}
          csvFileName="patient-records"
          list={(query) => patientRecordService.listPage(query)}
          loadRecordForEdit={(id) => patientRecordService.get(id)}
          create={(payload) => patientRecordService.create(toPatientPayload(payload))}
          update={(id, payload) =>
            patientRecordService.update(id, toPatientPayload(payload))
          }
          remove={(id) => patientRecordService.delete(id)}
        />
      )}

      {activeTab === "treatments" && (
        <ApiResourceTable<TreatmentSession>
          title="Treatment Register"
          labelField="patient_name"
          fields={treatmentFields}
          searchFields={["patient_name", "care_section", "doctor_assigned"]}
          defaultValues={{}}
          csvFileName="treatment-register"
          list={(query) => treatmentSessionService.listPage(query)}
          loadRecordForEdit={(id) => treatmentSessionService.get(id)}
          create={(payload) =>
            treatmentSessionService.create(toTreatmentPayload(payload))
          }
          update={(id, payload) =>
            treatmentSessionService.update(id, toTreatmentPayload(payload))
          }
          remove={(id) => treatmentSessionService.delete(id)}
        />
      )}

      {activeTab === "transfers" && (
        <ApiResourceTable<ReferralTransfer>
          title="Hospital Transfers"
          labelField="patient_name"
          fields={transferFields}
          searchFields={["patient_name", "partner_hospital", "coordinator_name"]}
          defaultValues={{
            referral_status: "Hospitalized",
          }}
          statusFilterOptions={[
            { value: "Hospitalized", label: "Hospitalized" },
            { value: "Discharged", label: "Discharged" },
            { value: "Under Observation", label: "Under Observation" },
          ]}
          csvFileName="hospital-transfers"
          list={(query) => referralTransferService.listPage(query)}
          loadRecordForEdit={(id) => referralTransferService.get(id)}
          create={(payload) =>
            referralTransferService.create(toTransferPayload(payload))
          }
          update={(id, payload) =>
            referralTransferService.update(id, toTransferPayload(payload))
          }
          remove={(id) => referralTransferService.delete(id)}
        />
      )}
      </Tabs>
    </AdminShell>
  );
}
